import { useState, useEffect, useRef } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import toast from 'react-hot-toast'
import { useAuth } from '../hooks/useAuth'
import { authAPI, hubplatformAPI, LOGOUT_REASON_IDLE, LOGOUT_REASON_KEY } from '../services/api'
import { AUTH_ERROR, isLoginOver, readAuthError } from '../services/authErrors'
import LanguageSwitcher from '../components/LanguageSwitcher'
import CodeForm from '../components/security/CodeForm'
import RecoveryCodes from '../components/security/RecoveryCodes'
import SecondFactorSetup from '../components/security/SecondFactorSetup'
import EnrolForm from '../components/security/EnrolForm'
import PasskeyPrompt from '../components/security/PasskeyPrompt'
import { passkeysSupported } from '../services/passkeys'
import { readSetupLink } from '../services/setupLink'
import { pathAfterLogin, rememberedUserId } from '../services/loginReturn'
import { HUBPLATFORM_RESULT, readHubPlatformLanding } from '../services/hubplatformLanding'
import HubPlatformButton from '../components/security/HubPlatformButton'
import EnvironmentBadge from '../components/EnvironmentBadge'

// VH-285: the login's steps. The password hands out a five-minute ticket;
// only the code (or a finished first set-up) logs in.
const STEP_PASSWORD = 'password'
const STEP_CODE = 'code'
const STEP_SETUP_CODE = 'setupCode'
const STEP_SETUP = 'setup'
const STEP_RECOVERY = 'recovery'
// VH-287: a password chosen with the set-up code from the mail (/set-up).
const STEP_ENROL = 'enrol'
const SETUP_PATH = '/set-up'

const SETUP_CODE_ERRORS = {
  [AUTH_ERROR.SETUP_CODE_WRONG]: 'secondFactor.setupCode.wrong',
  [AUTH_ERROR.SETUP_CODE_USED]: 'secondFactor.setupCode.used',
  [AUTH_ERROR.SETUP_CODE_EXPIRED]: 'secondFactor.setupCode.expired',
  [AUTH_ERROR.HELD]: 'auth.loginRefused',
  [AUTH_ERROR.NETWORK]: 'secondFactor.code.unreachable',
}

export default function Login() {
  const [loading, setLoading] = useState(false)
  const { login, finishLogin } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const { t } = useTranslation()
  const { register, handleSubmit, formState: { errors } } = useForm()
  // VH-284: the note after the idle countdown ended the last login. It stays
  // until the next login, so every tab that followed the log-out shows it.
  const [idleNote] = useState(() => {
    try {
      return localStorage.getItem(LOGOUT_REASON_KEY) === LOGOUT_REASON_IDLE
    } catch {
      return false
    }
  })

  // VH-293: a report page sent us here after its login ended; we go back there
  // only for the same person (read before the new login overwrites it).
  const [returnTo] = useState(() => location.state?.returnTo ?? null)
  const [previousUserId] = useState(() => rememberedUserId())

  // VH-287: the mail's link brings email and code after `#`; read once.
  const [fromMail] = useState(() => readSetupLink(location.hash))

  // VH-317: back from HubPlatform, `#hubplatform=<result>&handoff=…`; read once.
  const [fromHubPlatform] = useState(() => readHubPlatformLanding(location.hash))
  const [hubplatformOn, setHubplatformOn] = useState(false)
  // unreachable | refused | null: said under the button.
  const [hubplatformNote, setHubplatformNote] = useState(() => {
    const result = fromHubPlatform?.result
    return result === HUBPLATFORM_RESULT.UNREACHABLE || result === HUBPLATFORM_RESULT.REFUSED ? result : null
  })
  // "Confirm it's you once": the own login below links this HubPlatform sign-in.
  const [hubplatformLink, setHubplatformLink] = useState(() =>
    fromHubPlatform?.result === HUBPLATFORM_RESULT.NOT_LINKED ? fromHubPlatform.handoff : null,
  )
  const handedOff = useRef(false)

  // VH-285: where the login stands. The ticket lives in this component only.
  const [step, setStep] = useState(
    location.pathname === SETUP_PATH ? STEP_ENROL : STEP_PASSWORD,
  )
  const [ticket, setTicket] = useState(null)
  const [ticketEnds, setTicketEnds] = useState(null)
  const [timedOut, setTimedOut] = useState(false)
  const [setup, setSetup] = useState(null)
  const [activated, setActivated] = useState(null)
  const [setupCode, setSetupCode] = useState('')
  const [setupCodeError, setSetupCodeError] = useState(null)
  // VH-296: the password step said this user has a passkey here; the code
  // step then opens with the fingerprint or face, the code one tap away.
  const [passkeyOffered, setPasskeyOffered] = useState(false)
  const [withPasskey, setWithPasskey] = useState(false)

  // The code does not stay in the address bar or the history.
  useEffect(() => {
    if (location.hash) window.history.replaceState(null, '', location.pathname)
  }, [location.hash, location.pathname])

  const takeTicket = (next, seconds) => {
    setTicket(next)
    setTicketEnds(Date.now() + seconds * 1000)
  }

  const startAgain = (expired = true) => {
    setStep(STEP_PASSWORD)
    setTicket(null)
    setTicketEnds(null)
    setSetup(null)
    setActivated(null)
    setSetupCode('')
    setSetupCodeError(null)
    setPasskeyOffered(false)
    setWithPasskey(false)
    setTimedOut(expired)
  }

  // Five minutes after the password the ticket is good for nothing: back to
  // the start, said so. Not once the recovery codes show: the login is done.
  useEffect(() => {
    if (!ticketEnds || step === STEP_RECOVERY) return undefined
    const left = ticketEnds - Date.now()
    const timer = setTimeout(() => startAgain(true), Math.max(0, left))
    return () => clearTimeout(timer)
  }, [ticketEnds, step])

  const onSubmit = async (data) => {
    setLoading(true)
    setTimedOut(false)
    const result = await login(data)
    setLoading(false)
    if (!result.success) return
    takeTicket(result.step.ticket, result.step.expires_in)
    const offered = Boolean(result.step.passkey) && passkeysSupported()
    setPasskeyOffered(offered)
    setWithPasskey(offered)
    setStep(result.step.next === 'code' ? STEP_CODE : STEP_SETUP_CODE)
  }

  const loggedIn = (data) => {
    finishLogin({ token: data.token, user: data.user })
    if (data.used_recovery_code) {
      toast(t('secondFactor.login.recoveryLeft', { count: data.recovery_codes_left }))
    }
    if (data.hubplatform_linked === true) toast.success(t('hubplatform.linkedNow'))
    if (data.hubplatform_linked === false) toast.error(t('hubplatform.linkFailed'))
    navigate(pathAfterLogin(returnTo, previousUserId, data.user))
  }

  // VH-317: is the button on here.
  useEffect(() => {
    let current = true
    hubplatformAPI.status()
      .then(({ data }) => { if (current) setHubplatformOn(Boolean(data?.enabled)) })
      .catch(() => {})
    return () => { current = false }
  }, [])

  // VH-317: the hand-off is good once, so it is traded once (not again when
  // React runs the effect twice in development).
  useEffect(() => {
    const landing = fromHubPlatform
    if (!landing?.handoff || handedOff.current) return
    handedOff.current = true
    if (landing.result === HUBPLATFORM_RESULT.NOT_LINKED) {
      hubplatformAPI.confirmCheck(landing.handoff).catch(() => {
        setHubplatformLink(null)
        setHubplatformNote(HUBPLATFORM_RESULT.REFUSED)
      })
      return
    }
    if (landing.result !== HUBPLATFORM_RESULT.SIGNED_IN && landing.result !== HUBPLATFORM_RESULT.CODE) return
    setLoading(true)
    hubplatformAPI.finish(landing.handoff)
      .then(({ data }) => {
        if (data.outcome === HUBPLATFORM_RESULT.SIGNED_IN) {
          loggedIn(data.login)
          return
        }
        // HubPlatform's second step was missing or old: VitaHub's own.
        takeTicket(data.ticket.ticket, data.ticket.expires_in)
        const offered = Boolean(data.ticket.passkey) && passkeysSupported()
        setPasskeyOffered(offered)
        setWithPasskey(offered)
        setStep(STEP_CODE)
      })
      .catch(() => setHubplatformNote(HUBPLATFORM_RESULT.REFUSED))
      .finally(() => setLoading(false))
    // Once, on arrival: the landing is read once and the hand-off is good once.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const startSetup = async (withTicket, code) => {
    setLoading(true)
    setSetupCodeError(null)
    try {
      const { data } = await authAPI.beginSetup(withTicket, code)
      takeTicket(data.ticket, data.expires_in)
      setSetup(data)
      setStep(STEP_SETUP)
    } catch (err) {
      if (isLoginOver(err)) {
        startAgain(true)
        return
      }
      const { kind, status } = readAuthError(err)
      setSetupCode(code)
      setStep(STEP_SETUP_CODE)
      setSetupCodeError(t(SETUP_CODE_ERRORS[kind] || 'secondFactor.code.failed', { status }))
    } finally {
      setLoading(false)
    }
  }

  const submitSetupCode = (event) => {
    event.preventDefault()
    if (!setupCode.trim() || loading) return
    startSetup(ticket, setupCode.trim())
  }

  // VH-287: the password is chosen. With an authenticator its code is asked;
  // without one the same set-up code opens the set-up.
  const enrolled = (data, code) => {
    takeTicket(data.ticket, data.expires_in)
    if (data.next === 'code') setStep(STEP_CODE)
    else startSetup(data.ticket, code)
  }

  const titleKey = {
    [STEP_PASSWORD]: hubplatformLink ? 'hubplatform.confirmOnce.title' : 'auth.signInToAccount',
    [STEP_CODE]: withPasskey ? 'passkey.login.title' : 'secondFactor.login.codeTitle',
    [STEP_SETUP_CODE]: 'secondFactor.setupCode.title',
    [STEP_SETUP]: 'secondFactor.setup.heading',
    [STEP_RECOVERY]: 'secondFactor.recovery.heading',
    [STEP_ENROL]: 'enrol.title',
  }[step]

  const back = (
    <button type="button" onClick={() => startAgain(false)} className="btn-secondary w-full sm:flex-1">
      {t('secondFactor.login.back')}
    </button>
  )

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      {/* Language switcher in top right corner */}
      <div className="absolute top-4 right-4">
        <LanguageSwitcher />
      </div>
      {/* HP-60: which environment, before the login too (no header here). */}
      <div className="absolute left-4 top-4">
        <EnvironmentBadge />
      </div>
      <div className="max-w-md w-full space-y-8">
        <div>
          <h1 className="text-center text-4xl font-bold text-gradient">VitaHub</h1>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            {t(titleKey)}
          </h2>
        </div>
        {idleNote && step === STEP_PASSWORD && (
          <p role="status" className="rounded-md bg-yellow-50 p-3 text-center text-sm text-yellow-800">
            {t('session.loggedOutIdle')}
          </p>
        )}
        {timedOut && step === STEP_PASSWORD && (
          <p role="status" className="rounded-md bg-yellow-50 p-3 text-center text-sm text-yellow-800">
            {t('secondFactor.login.timedOut')}
          </p>
        )}

        {hubplatformLink && step === STEP_PASSWORD && (
          <div role="status" className="rounded-md bg-blue-50 p-3 text-sm text-blue-900">
            <p className="font-medium">{t('hubplatform.notLinked')}</p>
            <p className="mt-1">{t('hubplatform.confirmOnce.intro')}</p>
          </div>
        )}

        {step === STEP_PASSWORD && (
          <form className="mt-8 space-y-6" onSubmit={handleSubmit(onSubmit)}>
            <div className="space-y-4">
              <div>
                <label htmlFor="email" className="form-label">
                  {t('auth.email')}
                </label>
                <input
                  {...register('email', {
                    required: t('auth.emailRequired'),
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                      message: t('auth.invalidEmail')
                    }
                  })}
                  id="email"
                  type="email"
                  autoComplete="email"
                  className="form-input"
                />
                {errors.email && <p className="form-error">{errors.email.message}</p>}
              </div>
              <div>
                <label htmlFor="password" className="form-label">
                  {t('auth.password')}
                </label>
                <input
                  {...register('password', { required: t('auth.passwordRequired') })}
                  id="password"
                  type="password"
                  autoComplete="current-password"
                  className="form-input"
                />
                {errors.password && <p className="form-error">{errors.password.message}</p>}
              </div>
            </div>

            <div>
              <button
                type="submit"
                disabled={loading}
                className="btn-primary w-full py-3 text-base"
              >
                {loading ? t('auth.signingIn') : t('auth.signIn')}
              </button>
            </div>
            <p className="text-center text-sm">
              <button
                type="button"
                onClick={() => setStep(STEP_ENROL)}
                className="font-medium text-primary-600 hover:text-primary-500"
              >
                {t('enrol.haveCode')}
              </button>
            </p>
          </form>
        )}

        {step === STEP_PASSWORD && hubplatformOn && !hubplatformLink && (
          <HubPlatformButton note={hubplatformNote} />
        )}

        {step === STEP_ENROL && (
          <EnrolForm initialEmail={fromMail.email} initialCode={fromMail.code} onDone={enrolled}>
            {back}
          </EnrolForm>
        )}

        {step === STEP_CODE && withPasskey && (
          <div className="space-y-4">
            <p className="text-sm text-gray-700">{t('passkey.login.intro')}</p>
            <PasskeyPrompt
              idPrefix="login"
              ask={() => authAPI.passkeyOptions(ticket)}
              send={(credential) => authAPI.loginPasskey(ticket, credential, hubplatformLink)}
              onDone={loggedIn}
              onExpired={() => startAgain(true)}
            />
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <button
                type="button"
                onClick={() => setWithPasskey(false)}
                className="text-sm font-medium text-primary-600 underline hover:text-primary-500"
              >
                {t('passkey.login.useCode')}
              </button>
              {back}
            </div>
          </div>
        )}

        {step === STEP_CODE && !withPasskey && (
          <div className="space-y-4">
            <p className="text-sm text-gray-700">{t('secondFactor.login.codeIntro')}</p>
            <CodeForm
              idPrefix="login"
              send={(typed) => authAPI.loginCode(ticket, typed, hubplatformLink)}
              onDone={loggedIn}
              onExpired={() => startAgain(true)}
              submitLabel={t('auth.signIn')}
            >
              {back}
            </CodeForm>
            {passkeyOffered && (
              <button
                type="button"
                onClick={() => setWithPasskey(true)}
                className="text-sm font-medium text-primary-600 underline hover:text-primary-500"
              >
                {t('passkey.login.usePasskey')}
              </button>
            )}
          </div>
        )}

        {step === STEP_SETUP_CODE && (
          <form className="space-y-4" onSubmit={submitSetupCode} noValidate>
            <p className="text-sm text-gray-700">{t('secondFactor.setupCode.intro')}</p>
            <div>
              <label htmlFor="setup-code" className="form-label">
                {t('secondFactor.setupCode.label')}
              </label>
              <input
                id="setup-code"
                type="text"
                autoComplete="off"
                autoCapitalize="characters"
                spellCheck={false}
                value={setupCode}
                onChange={(event) => setSetupCode(event.target.value)}
                aria-invalid={Boolean(setupCodeError) || undefined}
                className="form-input font-mono tracking-wider"
              />
            </div>
            {setupCodeError && (
              <p role="alert" className="rounded bg-red-50 px-3 py-2 text-sm text-red-800">
                {setupCodeError}
              </p>
            )}
            <div className="flex flex-col-reverse gap-2 sm:flex-row">
              {back}
              <button type="submit" disabled={!setupCode.trim() || loading} className="btn-primary w-full sm:flex-1">
                {t('secondFactor.setupCode.submit')}
              </button>
            </div>
          </form>
        )}

        {step === STEP_SETUP && setup && (
          <SecondFactorSetup
            setup={setup}
            send={(code) => authAPI.activateSetup(ticket, code)}
            onDone={(data) => {
              setActivated(data)
              setStep(STEP_RECOVERY)
            }}
            onExpired={() => startAgain(true)}
          >
            {back}
          </SecondFactorSetup>
        )}

        {step === STEP_RECOVERY && activated && (
          <RecoveryCodes
            codes={activated.recovery_codes}
            onDone={() => loggedIn(activated.login)}
            doneLabel={t('secondFactor.recovery.toApp')}
          />
        )}
      </div>
    </div>
  )
}
